/**
 * When each thing was last changed.
 *
 * Groundwork for accounts. Once the same collection can be edited on a phone
 * and a laptop, something has to decide which version is the real one, and the
 * only honest answer is the one changed most recently. That answer has to be
 * recorded at the moment of the change — it cannot be worked out afterwards,
 * so it has to exist before the first sync, not alongside it.
 *
 * Kept in a sidecar key rather than wrapped around each value. Wrapping would
 * mean rewriting every stored shape and migrating collections that people
 * already have, to record something none of the readers care about. A sidecar
 * costs one extra key and leaves all of that alone.
 *
 * Nothing here uploads anything. This is a note of when, and nothing else.
 */

export const STAMPS_KEY = "ppt_stamps_v1";

/** Every stamp, as { storageKey: isoString }. Never throws. */
export function readStamps(store = window.localStorage) {
  try {
    const raw = store.getItem(STAMPS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/** When one key last changed, or null if it never has here. */
export function stampedAt(key, store = window.localStorage) {
  const at = readStamps(store)[key];
  return typeof at === "string" ? at : null;
}

/** Replace the whole map — used by a restore, which brings its own history. */
export function writeStamps(map, store = window.localStorage) {
  try {
    store.setItem(STAMPS_KEY, JSON.stringify(map || {}));
  } catch {
    /* a blocked localStorage loses the note, not the data */
  }
}

function record(key, at, store) {
  const map = readStamps(store);
  map[key] = at;
  writeStamps(map, store);
}

/**
 * Save a value and note when, but only if it actually changed.
 *
 * The comparison is the point, not an optimisation. Every hook here loads from
 * storage into state and then saves state back, so the first save after a page
 * load rewrites exactly what was just read. Stamping that would date all of a
 * person's data to whenever they last opened the app, and a device that had
 * only been *looked* at would then win every sync against one that had been
 * worked on.
 *
 * @returns true if something was written.
 */
export function save(key, raw, store = window.localStorage, now = () => new Date().toISOString()) {
  let current = null;
  try {
    current = store.getItem(key);
  } catch {
    /* unreadable storage is treated as different, so the write is attempted */
  }
  if (current === raw) return false;

  try {
    store.setItem(key, raw);
  } catch {
    return false;   // nothing was stored, so there is nothing to date
  }
  record(key, now(), store);
  return true;
}

/**
 * Forget a value, and note when.
 *
 * A deletion is a change like any other. Without a stamp, clearing a
 * collection on one device reads later as "this device has nothing to say",
 * and the copy that still has the cards wins — undoing the delete.
 */
export function drop(key, store = window.localStorage, now = () => new Date().toISOString()) {
  let existed = false;
  try {
    existed = store.getItem(key) != null;
    store.removeItem(key);
  } catch {
    return false;
  }
  if (existed) record(key, now(), store);
  return existed;
}
